import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'var(--app-viewport-height)',
  padding: '1.5rem',
  fontFamily: "'Lexend', system-ui, sans-serif",
  textAlign: 'center',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.25rem',
  marginBottom: '0.5rem',
};

const messageStyle: React.CSSProperties = {
  color: '#666',
  fontSize: '0.875rem',
  maxWidth: '28rem',
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  marginTop: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const buttonStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  border: '1px solid #ccc',
  borderRadius: '4px',
  background: '#fff',
  cursor: 'pointer',
};

const primaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  borderColor: '#3182ce',
  background: '#3182ce',
  color: '#fff',
};

interface ErrorFallbackContentProps {
  error: Error;
  onRetry?: () => void;
}

function ErrorFallbackContent({ error, onRetry }: ErrorFallbackContentProps) {
  const isDev = import.meta.env.DEV;

  return (
    <div style={containerStyle}>
      <h1 style={headingStyle}>Something went wrong</h1>
      <p style={messageStyle}>{error.message}</p>
      {isDev && onRetry && (
        <div style={buttonGroupStyle}>
          <button type="button" style={primaryButtonStyle} onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
    </div>
  );
}

export class RootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('RootErrorBoundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <ErrorFallbackContent
          error={this.state.error}
          onRetry={
            import.meta.env.DEV
              ? () => this.setState({ hasError: false, error: null })
              : undefined
          }
        />
      );
    }
    return this.props.children;
  }
}
